import { supabase } from "../supabaseClient";

// --- Auth headers helper (same pattern as apiService.js) ---
async function getAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) return null;
  return { Authorization: `Bearer ${token}` };
}

async function authFetch(url, options = {}) {
  const authHeaders = await getAuthHeaders();
  if (!authHeaders) return null;
  const res = await fetch(url, {
    ...options,
    headers: { ...options.headers, ...authHeaders },
  });
  if (res.status === 401) return null;
  return res;
}

// --- Topics ---
export async function fetchTopics(category, page = 1) {
  let url = `/api/topics?page=${page}`;
  if (category) url += `&category=${category}`;
  const res = await authFetch(url);
  if (!res || !res.ok) return [];
  return res.json();
}

export async function fetchTopicDetail(topicId) {
  const res = await authFetch(`/api/topics?id=${topicId}`);
  if (!res || !res.ok) return null;
  return res.json();
}

export async function createTopic(title, body, category) {
  const res = await authFetch("/api/topics", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title, body, category }),
  });
  if (!res) return null;
  if (res.status === 429) throw new Error("rate_limit");
  if (!res.ok) throw new Error("create_failed");
  return res.json();
}

export async function deleteTopic(topicId) {
  const res = await authFetch(`/api/topics?id=${topicId}`, { method: "DELETE" });
  if (!res || !res.ok) return false;
  return true;
}

// --- Replies (merged into /api/topics) ---
export async function createReply(topicId, body) {
  const res = await authFetch("/api/topics?action=reply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ topic_id: topicId, body }),
  });
  if (!res) return null;
  if (res.status === 429) throw new Error("rate_limit");
  if (!res.ok) throw new Error("reply_failed");
  return res.json();
}

export async function deleteReply(replyId) {
  const res = await authFetch(`/api/topics?reply_id=${replyId}`, { method: "DELETE" });
  if (!res || !res.ok) return false;
  return true;
}

// --- Chat ---
export async function fetchChatHistory(before, limit = 50) {
  let url = `/api/chat?limit=${limit}`;
  if (before) url += `&before=${encodeURIComponent(before)}`;
  const res = await authFetch(url);
  if (!res || !res.ok) return [];
  return res.json();
}

export async function sendChatMessage(body) {
  const res = await authFetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });
  if (!res) return null;
  if (res.status === 429) throw new Error("rate_limit");
  if (!res.ok) throw new Error("send_failed");
  return res.json();
}

// --- Image Upload (merged into /api/community?action=upload) ---
export async function uploadImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result.split(",")[1];
        const res = await authFetch("/api/community?action=upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            data: base64,
            contentType: file.type,
          }),
        });
        if (!res) { reject(new Error("auth")); return; }
        if (res.status === 429) { reject(new Error("rate_limit")); return; }
        if (!res.ok) { reject(new Error("upload_failed")); return; }
        const data = await res.json();
        resolve(data.url);
      } catch (e) { reject(e); }
    };
    reader.onerror = () => reject(new Error("read_failed"));
    reader.readAsDataURL(file);
  });
}

// --- Profile (merged into /api/community?action=profile) ---
export async function fetchMyProfile() {
  const res = await authFetch("/api/community?action=profile");
  if (!res || !res.ok) return null;
  return res.json();
}

export async function updateProfile(displayName) {
  const res = await authFetch("/api/community", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ display_name: displayName }),
  });
  if (!res || !res.ok) return null;
  return res.json();
}
